import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import IustiaLogo from '../../../assets/images/logos/iustia-logo.svg';
import IustiaLogoWhite from '../../../assets/images/logos/iustia-logo-white.svg';

const Footer = () => {
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = section => {
    setOpenSection(openSection === section ? null : section);
  };

  // Animation variants following codebase patterns
  const containerVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.2,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  const logoVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.25,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  const contentVariants = {
    hidden: {
      height: 0,
      opacity: 0,
    },
    visible: {
      height: 'auto',
      opacity: 1,
      transition: {
        height: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
        opacity: { duration: 0.15 },
      },
    },
    exit: {
      height: 0,
      opacity: 0,
      transition: {
        height: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
        opacity: { duration: 0.15 },
      },
    },
  };

  return (
    <motion.footer
      className="bg-custom-dark text-white w-full"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
    >
      {/* AETHER Style Header */}
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-8">
        <motion.div className="text-center mb-16" variants={itemVariants}>
          <motion.img
            src={IustiaLogoWhite}
            alt="Logo de Iustia"
            className="h-16 mx-auto mb-8"
            variants={logoVariants}
            whileHover={{ scale: 1.05 }}
          />
        </motion.div>

        <motion.hr className="border-t border-[#334E6A] opacity-30 mb-8" variants={itemVariants} />

        {/* AETHER Style Navigation Sections */}
        <motion.div className="space-y-0" variants={itemVariants}>
          {/* Servicios Section */}
          <motion.div className="border-b border-[#334E6A] opacity-30" variants={itemVariants}>
            <motion.button
              onClick={() => toggleSection('servicios')}
              className="w-full py-6 flex justify-between items-center text-left text-xl font-bold tracking-wide hover:opacity-75 transition-opacity text-white"
              whileHover={{ x: 5 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>Servicios</span>
            </motion.button>
            <AnimatePresence initial={false}>
              {openSection === 'servicios' && (
                <motion.div
                  key="content"
                  className="overflow-hidden"
                  variants={contentVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{ overflow: 'hidden' }}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.3 }}
                    className="pb-6"
                  >
                    <ul className="space-y-3 text-sm pl-4">
                      <li>
                        <button className="hover:underline opacity-75 hover:opacity-100 transition-opacity text-white font-bold">
                          Abogados
                        </button>
                      </li>
                      <li>
                        <button className="hover:underline opacity-75 hover:opacity-100 transition-opacity text-white font-bold">
                          Consultas
                        </button>
                      </li>
                    </ul>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Publicaciones Section */}
          <motion.div className="border-b border-[#334E6A] opacity-30" variants={itemVariants}>
            <motion.button
              onClick={() => toggleSection('publicaciones')}
              className="w-full py-6 flex justify-between items-center text-left text-xl font-bold tracking-wide hover:opacity-75 transition-opacity text-white"
              whileHover={{ x: 5 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>Publicaciones</span>
            </motion.button>
            <AnimatePresence initial={false}>
              {openSection === 'publicaciones' && (
                <motion.div
                  key="content"
                  className="overflow-hidden"
                  variants={contentVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{ overflow: 'hidden' }}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.3 }}
                    className="pb-6"
                  >
                    <ul className="space-y-3 text-sm pl-4">
                      <li>
                        <button className="hover:underline opacity-75 hover:opacity-100 transition-opacity text-white font-bold">
                          Artículos Legales
                        </button>
                      </li>
                      <li>
                        <button className="hover:underline opacity-75 hover:opacity-100 transition-opacity text-white font-bold">
                          Noticias Jurídicas
                        </button>
                      </li>
                    </ul>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* ¿Quiénes Somos? Section */}
          <motion.div className="border-b border-[#334E6A] opacity-30" variants={itemVariants}>
            <motion.button
              onClick={() => toggleSection('quienes-somos')}
              className="w-full py-6 flex justify-between items-center text-left text-xl font-bold tracking-wide hover:opacity-75 transition-opacity text-white"
              whileHover={{ x: 5 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>¿Quiénes Somos?</span>
            </motion.button>
            <AnimatePresence initial={false}>
              {openSection === 'quienes-somos' && (
                <motion.div
                  key="content"
                  className="overflow-hidden"
                  variants={contentVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{ overflow: 'hidden' }}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.3 }}
                    className="pb-6"
                  >
                    <ul className="space-y-3 text-sm pl-4">
                      <li>
                        <button className="hover:underline opacity-75 hover:opacity-100 transition-opacity text-white font-bold">
                          Nuestra Misión
                        </button>
                      </li>
                      <li>
                        <button className="hover:underline opacity-75 hover:opacity-100 transition-opacity text-white font-bold">
                          Nuestro Equipo
                        </button>
                      </li>
                      <li>
                        <button className="hover:underline opacity-75 hover:opacity-100 transition-opacity text-white font-bold">
                          Contacto
                        </button>
                      </li>
                    </ul>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Soporte Section */}
          <motion.div variants={itemVariants}>
            <motion.button
              onClick={() => toggleSection('soporte')}
              className="w-full py-6 flex justify-between items-center text-left text-xl font-bold tracking-wide hover:opacity-75 transition-opacity text-white"
              whileHover={{ x: 5 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>Soporte</span>
            </motion.button>
            <AnimatePresence initial={false}>
              {openSection === 'soporte' && (
                <motion.div
                  key="content"
                  className="overflow-hidden"
                  variants={contentVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{ overflow: 'hidden' }}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.3 }}
                    className="pb-6"
                  >
                    <ul className="space-y-3 text-sm pl-4">
                      <li>
                        <button className="hover:underline opacity-75 hover:opacity-100 transition-opacity text-white font-bold">
                          Preguntas Frecuentes
                        </button>
                      </li>
                      <li>
                        <button className="hover:underline opacity-75 hover:opacity-100 transition-opacity text-white font-bold">
                          Términos y Condiciones
                        </button>
                      </li>
                      <li>
                        <button className="hover:underline opacity-75 hover:opacity-100 transition-opacity text-white font-bold">
                          Políticas de Privacidad
                        </button>
                      </li>
                    </ul>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>

        <motion.hr className="border-t border-[#334E6A] opacity-30 my-12" variants={itemVariants} />

        {/* Bottom Section - AETHER Style */}
        <motion.div className="text-center space-y-6" variants={itemVariants}>
          <motion.div className="flex justify-center" variants={itemVariants}>
            <motion.div
              className="w-12 h-12 border border-white rounded-full flex items-center justify-center"
              whileHover={{ scale: 1.1, rotate: 180 }}
              transition={{ duration: 0.3 }}
            >
              <img src={IustiaLogo} alt="Icono de Iustia" className="h-6 w-6 filter invert" />
            </motion.div>
          </motion.div>

          <motion.div
            className="text-xs opacity-50 uppercase tracking-wider"
            variants={itemVariants}
          >
            © 2025 TODOS LOS DERECHOS RESERVADOS
          </motion.div>

          <motion.div className="space-y-2 text-xs" variants={itemVariants}>
            <div>
              <motion.button
                className="underline hover:opacity-75 transition-opacity text-white font-bold"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                PRIVACIDAD
              </motion.button>
            </div>
            <div>
              <motion.button
                className="underline hover:opacity-75 transition-opacity text-white font-bold"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                TÉRMINOS DE USO
              </motion.button>
            </div>
          </motion.div>

          <motion.div
            className="text-xs opacity-75 max-w-md mx-auto leading-relaxed pt-8"
            variants={itemVariants}
          >
            SOLO VIVES UNA VEZ, PERO SI LO HACES BIEN, UNA VEZ ES SUFICIENTE. - IUSTIA
          </motion.div>
        </motion.div>
      </div>
    </motion.footer>
  );
};

export default Footer;
